/**
 * Product Image URL Parser
 *
 * Implements the complete URL transformation logic for product images as specified
 * in the Product Image URL Logic documentation. This function processes raw URLs
 * from the database and transforms them into appropriate image sources, link URLs,
 * and metadata based on the URL format and product properties.
 *
 * @param url - The raw URL string from the database (can be null, undefined, empty, full URL, or relative path)
 * @param options - Optional configuration object
 * @returns Object containing imageSrc, linkUrl, description, and altText
 */

export interface ParseProductImageUrlOptions {
  /**
   * If true, disables clickable links (for print mode)
   */
  isPrintMode?: boolean;

  /**
   * View type: 'summary' (indicator only), 'advanced' (full transformation), or 'detail' (full transformation)
   */
  viewType?: 'summary' | 'advanced' | 'detail';

  /**
   * Custom placeholder path for "no image available" (defaults to '/assets/svgs/fallback.svg')
   */
  noImagePlaceholder?: string;

  /**
   * Custom placeholder path for website icon (defaults to '/assets/images/viewweb.gif' or similar)
   */
  websiteIconPlaceholder?: string;

  /**
   * Product description for alt text (optional)
   */
  productDescription?: string;
}

export interface ParseProductImageUrlResult {
  /**
   * The image source URL to use in <img src={imageSrc} />
   */
  imageSrc: string;

  /**
   * The link URL for clickable images (empty string if not clickable)
   */
  linkUrl: string;

  /**
   * Description text for the link/image
   */
  imageDescription: string;

  /**
   * Alt text for accessibility
   */
  altText: string;

  /**
   * Whether the URL represents an actual product image (true) or a website link (false)
   */
  isImage: boolean;

  /**
   * Whether a URL exists (not empty)
   */
  hasUrl: boolean;
}

/**
 * Base domain URL for prepending relative paths
 * Defaults to the current domain (window.location.origin) if not specified in env
 */
// const BASE_DOMAIN_URL = get(
//   process,
//   'env.REACT_APP_IMAGE_BASE_URL',
//   typeof window !== 'undefined' ? window.location.origin : ''
// );
// const BASE_DOMAIN_URL =
//   (typeof process !== 'undefined' && `${process.env?.REACT_APP_LEGACY_URL}/Content/Resources/`) ||
//   (typeof window !== 'undefined' ? window.location.origin : '');
const BASE_DOMAIN_URL = process.env.REACT_APP_LEGACY_URL
  ? `${process.env.REACT_APP_LEGACY_URL}/Content/Resources/`
  : typeof window !== 'undefined'
  ? window.location.origin
  : '';
/**
 * Image file extensions to detect (case-insensitive)
 * Note: .jp matches .jpg, .jpeg, .jpe (any extension starting with .jp)
 * .jfif is handled separately as it contains .jf, not .jp
 */
const IMAGE_EXTENSIONS = ['.gif', '.jp', '.jfif', '.bmp', '.png', '.tif', '.tiff'];

/**
 * Default placeholder paths
 */
export const DEFAULT_PLACEHOLDERS = {
  noImage: '/assets/svgs/fallback.svg',
  websiteIcon: '/assets/svgs/fallback.svg',
  brokenImage: '/assets/svgs/fallback-broken.svg',
};

/**
 * Checks if a URL contains an image file extension (case-insensitive)
 */
function hasImageExtension(url: string): boolean {
  const lowerUrl = url.toLowerCase();
  return IMAGE_EXTENSIONS.some((ext) => lowerUrl.includes(ext));
}

/**
 * Checks if a URL starts with http:// or https:// (case-insensitive)
 */
function isAbsoluteUrl(url: string): boolean {
  const lowerUrl = url.toLowerCase();
  return lowerUrl.startsWith('http://') || lowerUrl.startsWith('https://');
}

/**
 * Normalizes a URL by handling null/undefined and trimming whitespace
 */
function normalizeUrl(url: string | null | undefined): string {
  if (url === null || url === undefined) {
    return '';
  }
  return url.trim();
}

/**
 * Ensures a URL has http:// or https:// prefix. If it already has one, returns as-is.
 * For relative paths (both "pictures/catalogs/WPoint.gif" and "/pictures/catalogs/WPoint.gif" formats),
 * prepends BASE_DOMAIN_URL.
 */
function ensureAbsoluteUrl(url: string): string {
  if (isAbsoluteUrl(url)) {
    return url;
  }
  // Remove leading slash if present (both "pictures/..." and "/pictures/..." are treated as relative)
  const normalizedPath = url.startsWith('/') ? url.slice(1) : url;
  // Relative paths should be prepended with BASE_DOMAIN_URL
  const baseUrl = BASE_DOMAIN_URL.endsWith('/') ? BASE_DOMAIN_URL : `${BASE_DOMAIN_URL}/`;
  return `${baseUrl}${normalizedPath}`;
}

/**
 * Main function to parse product image URLs
 */
export function parseProductImageUrl(
  url: string | null | undefined,
  options: ParseProductImageUrlOptions = {}
): ParseProductImageUrlResult {
  const {
    isPrintMode = false,
    viewType = 'advanced',
    noImagePlaceholder = DEFAULT_PLACEHOLDERS.noImage,
    productDescription = '',
  } = options;

  // Step 1: Normalize the input
  const normalizedUrl = normalizeUrl(url);
  const hasUrl = normalizedUrl !== '';

  // Step 2: Summary view - only check if URL exists
  if (viewType === 'summary') {
    return {
      imageSrc: '',
      linkUrl: '',
      imageDescription: hasUrl ? 'P' : '',
      altText: hasUrl ? 'Product image available' : 'No product image',
      isImage: false,
      hasUrl,
    };
  }

  // Step 3: Check for empty URL
  if (!hasUrl) {
    return {
      imageSrc: noImagePlaceholder,
      linkUrl: noImagePlaceholder,
      imageDescription: 'N/A',
      altText: 'No image available',
      isImage: false,
      hasUrl: false,
    };
  }

  // Step 4: Check if URL contains image file extension
  const containsImageExtension = hasImageExtension(normalizedUrl);

  if (containsImageExtension) {
    // Rule 1: URL contains image file extension
    const absoluteUrl = ensureAbsoluteUrl(normalizedUrl);
    const linkUrl = isPrintMode ? '' : absoluteUrl;

    return {
      imageSrc: absoluteUrl,
      linkUrl,
      imageDescription: productDescription || 'Product image',
      altText: productDescription || 'Product image',
      isImage: true,
      hasUrl: true,
    };
  } else {
    // Rule 3: URL is not an image (website link)
    const absoluteUrl = ensureAbsoluteUrl(normalizedUrl);
    const linkUrl = isPrintMode ? '' : absoluteUrl;

    return {
      imageSrc: linkUrl,
      linkUrl,
      imageDescription: 'View website',
      altText: 'View product website',
      isImage: false,
      hasUrl: true,
    };
  }
}

/**
 * Helper function to check if a product has a URL (for summary view indicator)
 */
export function hasProductUrl(url: string | null | undefined): boolean {
  return normalizeUrl(url) !== '';
}

/**
 * Helper function to get just the image source URL (simplified version)
 */
export function getProductImageSrc(
  url: string | null | undefined,
  options: Omit<ParseProductImageUrlOptions, 'viewType'> & {
    viewType?: ParseProductImageUrlOptions['viewType'];
  } = {}
): string {
  const result = parseProductImageUrl(url, {
    ...options,
    viewType: options.viewType || 'advanced',
  });
  return result.imageSrc;
}
